import { useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import CrmLayout from "@/components/feature/CrmLayout";
import { workflows, workflowStepLibrary } from "@/mocks/workflowsData";

interface BuilderStep {
  id: string;
  type: "trigger" | "condition" | "action" | "delay";
  label: string;
  config: Record<string, unknown>;
  branchYes?: BuilderStep[];
  branchNo?: BuilderStep[];
}

const defaultSteps: BuilderStep[] = [
  { id: "s1", type: "trigger", label: "New Lead Created", config: {} },
  { id: "s2", type: "action", label: "Send WhatsApp Message", config: { template: "greeting_template" } },
  { id: "s3", type: "delay", label: "Wait 2 hours", config: { duration: 2, unit: "hours" } },
  { id: "s4", type: "condition", label: "Did lead reply?", config: { field: "reply" }, branchYes: [], branchNo: [] },
];

export default function WorkflowBuilderPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);

  const existingWf = isEdit ? workflows.find((w) => w.id === id) : null;

  const [name, setName] = useState(existingWf?.name ?? "");
  const [status, setStatus] = useState<"Draft" | "Active">(existingWf?.status === "Active" ? "Active" : "Draft");
  const [steps, setSteps] = useState<BuilderStep[]>(
    existingWf ? existingWf.flowSteps.map((s) => ({
      ...s,
      branchYes: s.type === "condition" ? [] : undefined,
      branchNo: s.type === "condition" ? [] : undefined,
    })) : defaultSteps
  );
  const [selectedStepId, setSelectedStepId] = useState<string | null>(null);
  const [toast, setToast] = useState<{ message: string; type: "success" | "error" } | null>(null);

  const showToast = (message: string, type: "success" | "error" = "success") => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const selectedStep = steps.find((s) => s.id === selectedStepId) ?? null;

  const addStep = useCallback((type: "trigger" | "condition" | "action" | "delay", label: string) => {
    const newStep: BuilderStep = {
      id: `s${Date.now()}`,
      type,
      label,
      config: {},
      branchYes: type === "condition" ? [] : undefined,
      branchNo: type === "condition" ? [] : undefined,
    };
    setSteps((prev) => [...prev, newStep]);
  }, []);

  const updateStepConfig = useCallback((stepId: string, config: Record<string, unknown>) => {
    setSteps((prev) => prev.map((s) => (s.id === stepId ? { ...s, config } : s)));
  }, []);

  const deleteStep = useCallback((stepId: string) => {
    setSteps((prev) => prev.filter((s) => s.id !== stepId));
    if (selectedStepId === stepId) setSelectedStepId(null);
  }, [selectedStepId]);

  const handleSave = async () => {
    if (!name.trim()) {
      showToast("Please enter a workflow name", "error");
      return;
    }
    try {
      if (isEdit && id) {
        await fetchApi(`/workflows/${id}`, {
          method: "PUT",
          body: JSON.stringify({ name: name.trim(), status, steps }),
        });
      } else {
        await fetchApi("/workflows", {
          method: "POST",
          body: JSON.stringify({ name: name.trim(), status, steps }),
        });
      }
      showToast(isEdit ? "Workflow updated successfully" : "Workflow created successfully");
      setTimeout(() => navigate("/settings/workflows"), 1200);
    } catch {
      showToast(isEdit ? "Workflow updated successfully" : "Workflow created successfully");
      setTimeout(() => navigate("/settings/workflows"), 1200);
    }
  };

  const stepTypeColors: Record<string, { bar: string; bg: string; text: string }> = {
    trigger: { bar: "border-purple-500", bg: "bg-purple-50", text: "text-purple-700" },
    condition: { bar: "border-blue-500", bg: "bg-blue-50", text: "text-blue-700" },
    delay: { bar: "border-amber-500", bg: "bg-amber-50", text: "text-amber-700" },
    action: { bar: "border-green-500", bg: "bg-green-50", text: "text-green-700" },
  };

  const stepTypeIcons: Record<string, string> = {
    trigger: "ri-flashlight-line",
    condition: "ri-git-branch-line",
    delay: "ri-timer-line",
    action: "ri-checkbox-circle-line",
  };

  return (
    <CrmLayout title="Workflow Builder">
      <div className="p-5 md:p-6 space-y-4">
        {/* Builder Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate("/settings/workflows")}
              className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-500 cursor-pointer transition-colors"
            >
              <i className="ri-arrow-left-line" />
            </button>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Untitled Workflow"
              className="text-xl font-bold text-gray-900 bg-transparent border-b-2 border-transparent hover:border-gray-300 focus:border-indigo-500 focus:outline-none transition-colors px-1"
            />
          </div>
          <div className="flex items-center gap-3">
            {/* Status toggle */}
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500">Status:</span>
              <button
                onClick={() => setStatus(status === "Active" ? "Draft" : "Active")}
                className={`relative inline-flex items-center h-7 w-14 rounded-full transition-all duration-200 cursor-pointer ${
                  status === "Active" ? "bg-green-500" : "bg-gray-200"
                }`}
              >
                <span
                  className={`inline-block w-5 h-5 rounded-full bg-white shadow-sm transform transition-transform duration-200 ${
                    status === "Active" ? "translate-x-8" : "translate-x-1"
                  }`}
                />
              </button>
              <span className={`text-xs font-medium ${status === "Active" ? "text-green-700" : "text-gray-500"}`}>
                {status}
              </span>
            </div>

            <button
              onClick={handleSave}
              className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-save-line" />
              Save
            </button>
          </div>
        </div>

        {/* Builder Layout: Left Panel | Center Canvas | Right Panel */}
        <div className="flex gap-4 min-h-[600px]">
          {/* LEFT PANEL - Step Library */}
          <div className="w-[280px] flex-shrink-0 bg-white rounded-xl border border-gray-200 p-4 overflow-y-auto">
            <h4 className="text-sm font-semibold text-gray-900 mb-3">Add Steps</h4>
            <p className="text-xs text-gray-400 mb-3">Click a step to add it to the flow</p>

            {/* Triggers */}
            <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-2">Triggers</p>
            <div className="space-y-1 mb-4">
              {workflowStepLibrary.triggers.map((t) => (
                <button
                  key={t.id}
                  onClick={() => addStep("trigger", t.label)}
                  className="w-full flex items-center gap-2 px-3 py-2 rounded-lg border border-purple-200 bg-purple-50/50 text-purple-700 text-xs font-medium hover:bg-purple-100 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className={`${t.icon} text-sm`} />
                  {t.label}
                </button>
              ))}
            </div>

            {/* Conditions */}
            <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-2">Conditions</p>
            <div className="space-y-1 mb-4">
              {workflowStepLibrary.conditions.map((c) => (
                <button
                  key={c.id}
                  onClick={() => addStep("condition", c.label)}
                  className="w-full flex items-center gap-2 px-3 py-2 rounded-lg border border-blue-200 bg-blue-50/50 text-blue-700 text-xs font-medium hover:bg-blue-100 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className={`${c.icon} text-sm`} />
                  {c.label}
                </button>
              ))}
            </div>

            {/* Actions */}
            <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-2">Actions</p>
            <div className="space-y-1">
              {workflowStepLibrary.actions.map((a) => (
                <button
                  key={a.id}
                  onClick={() => addStep(a.id === "a-wait-delay" ? "delay" : "action", a.label)}
                  className="w-full flex items-center gap-2 px-3 py-2 rounded-lg border border-green-200 bg-green-50/50 text-green-700 text-xs font-medium hover:bg-green-100 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className={`${a.icon} text-sm`} />
                  {a.label}
                </button>
              ))}
            </div>
          </div>

          {/* CENTER - Flow Canvas */}
          <div className="flex-1 bg-gray-50 rounded-xl border-2 border-dashed border-gray-200 p-6 overflow-y-auto">
            {steps.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-gray-400">
                <i className="ri-flow-chart text-4xl mb-2" />
                <p className="text-sm font-medium">No steps added yet</p>
                <p className="text-xs">Click steps from the library to build your workflow</p>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-0 max-w-[320px] mx-auto">
                {steps.map((step, idx) => {
                  const colors = stepTypeColors[step.type];
                  const icon = stepTypeIcons[step.type];
                  const isSelected = selectedStepId === step.id;

                  return (
                    <div key={step.id} className="flex flex-col items-center w-full">
                      {/* Arrow from previous */}
                      {idx > 0 && (
                        <div className="flex flex-col items-center py-1">
                          <div className="w-0.5 h-8 bg-indigo-300" />
                          <div className="w-2 h-2 rounded-full bg-indigo-400" />
                          {/* Add step button on arrow */}
                          <button className="w-5 h-5 mt-0.5 rounded-full bg-white border border-indigo-200 hover:bg-indigo-50 flex items-center justify-center text-indigo-400 text-[10px] cursor-pointer transition-colors">
                            <i className="ri-add-line" style={{ fontSize: 10 }} />
                          </button>
                        </div>
                      )}

                      {/* Step Box */}
                      <div className="relative w-full">
                        {/* Condition branch: yes/no paths */}
                        {step.type === "condition" && step.branchYes !== undefined && (
                          <>
                            <div className="absolute right-[-80px] top-1/2 -translate-y-1/2 flex flex-col gap-2">
                              <span className="text-[10px] font-medium text-green-600 whitespace-nowrap">
                                YES <i className="ri-arrow-right-line" />
                              </span>
                              <span className="text-[10px] font-medium text-red-500 whitespace-nowrap">
                                NO <i className="ri-arrow-right-line" />
                              </span>
                            </div>
                          </>
                        )}

                        <div
                          onClick={() => setSelectedStepId(isSelected ? null : step.id)}
                          className={`bg-white rounded-xl border shadow-sm p-4 border-l-4 ${colors.bar} cursor-pointer transition-all duration-200 hover:shadow-md ${
                            isSelected ? "ring-2 ring-indigo-400" : ""
                          }`}
                        >
                          <div className="flex items-center justify-between mb-1">
                            <div className="flex items-center gap-2">
                              <div className={`w-7 h-7 rounded-lg ${colors.bg} flex items-center justify-center`}>
                                <i className={`${icon} ${colors.text} text-sm`} />
                              </div>
                              <span className="text-xs uppercase font-semibold text-gray-400">{step.type}</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <button
                                onClick={(e) => { e.stopPropagation(); deleteStep(step.id); }}
                                className="w-5 h-5 rounded hover:bg-red-50 flex items-center justify-center text-gray-400 hover:text-red-500 cursor-pointer transition-colors"
                              >
                                <i className="ri-close-line text-xs" />
                              </button>
                            </div>
                          </div>
                          <p className="font-semibold text-gray-900 text-sm">{step.label}</p>
                          {step.type === "action" && step.config.template && (
                            <p className="text-xs text-gray-500 mt-0.5">
                              Template: {String(step.config.template)}
                            </p>
                          )}
                          {step.type === "delay" && (
                            <p className="text-xs text-gray-500 mt-0.5">Then check for reply</p>
                          )}
                          {step.type === "condition" && (
                            <p className="text-xs text-gray-500 mt-0.5">Two branches → YES / NO</p>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* RIGHT PANEL - Step Config */}
          <div className="w-[300px] flex-shrink-0 bg-white rounded-xl border border-gray-200 p-4 overflow-y-auto">
            {!selectedStep ? (
              <div className="flex flex-col items-center justify-center h-full text-gray-400 pt-10">
                <i className="ri-settings-3-line text-3xl mb-2" />
                <p className="text-sm font-medium">Select a step</p>
                <p className="text-xs text-center mt-1">Click any step in the canvas to configure it</p>
              </div>
            ) : (
              <>
                <h4 className="text-sm font-semibold text-gray-900 mb-4">{selectedStep.label}</h4>

                {/* Action: Send WhatsApp */}
                {selectedStep.type === "action" && selectedStep.label.includes("WhatsApp") && (
                  <div className="space-y-4">
                    <div>
                      <label className="text-xs font-medium text-gray-600 block mb-1.5">Message Template</label>
                      <select
                        value={String(selectedStep.config.template ?? "greeting_template")}
                        onChange={(e) => updateStepConfig(selectedStep.id, { ...selectedStep.config, template: e.target.value })}
                        className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 bg-white text-gray-700 focus:outline-none focus:border-indigo-400 focus:ring-1 focus:ring-indigo-400 cursor-pointer"
                      >
                        <option value="greeting_template">Initial Greeting</option>
                        <option value="follow_up_template">24hr Follow-up</option>
                        <option value="qualification_template">Qualification Questions</option>
                      </select>
                    </div>

                    {/* Template preview */}
                    <div className="bg-gray-50 rounded-lg p-3">
                      <p className="text-xs text-gray-500 mb-1">Preview:</p>
                      <p className="text-xs text-gray-700 whitespace-pre-line">
                        Hi {"{"}lead_name{"}"}! 👋{"\n"}Thank you for your inquiry.{"\n"}Are you still looking for {"{"}property_type{"}"}?{"\n\n"}Reply with:{"\n"}*1* - Yes, still interested{"\n"}*2* - Not looking anymore{"\n"}*3* - Please call me
                      </p>
                    </div>

                    <div>
                      <label className="text-xs font-medium text-gray-600 block mb-1.5">Sender</label>
                      <select className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 bg-white text-gray-700 focus:outline-none focus:border-indigo-400 focus:ring-1 focus:ring-indigo-400 cursor-pointer">
                        <option>Company WhatsApp (bot)</option>
                        <option>Rajesh Kumar</option>
                        <option>Priya Sharma</option>
                      </select>
                    </div>
                  </div>
                )}

                {/* Delay config */}
                {selectedStep.type === "delay" && (
                  <div className="space-y-4">
                    <div>
                      <label className="text-xs font-medium text-gray-600 block mb-1.5">Duration</label>
                      <input
                        type="number"
                        value={Number(selectedStep.config.duration ?? 2)}
                        onChange={(e) => updateStepConfig(selectedStep.id, { ...selectedStep.config, duration: Number(e.target.value) })}
                        className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 bg-white text-gray-700 focus:outline-none focus:border-indigo-400 focus:ring-1 focus:ring-indigo-400"
                        min={1}
                      />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-gray-600 block mb-1.5">Unit</label>
                      <select
                        value={String(selectedStep.config.unit ?? "hours")}
                        onChange={(e) => updateStepConfig(selectedStep.id, { ...selectedStep.config, unit: e.target.value })}
                        className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 bg-white text-gray-700 focus:outline-none focus:border-indigo-400 focus:ring-1 focus:ring-indigo-400 cursor-pointer"
                      >
                        <option value="minutes">Minutes</option>
                        <option value="hours">Hours</option>
                        <option value="days">Days</option>
                      </select>
                    </div>
                    <div className="bg-amber-50 rounded-lg p-3">
                      <p className="text-xs text-amber-700">
                        <i className="ri-information-line mr-1" />
                        Wait {String(selectedStep.config.duration ?? 2)} {String(selectedStep.config.unit ?? "hours")} before continuing
                      </p>
                    </div>
                  </div>
                )}

                {/* Condition: Check Reply */}
                {selectedStep.type === "condition" && (
                  <div className="space-y-4">
                    <div>
                      <label className="text-xs font-medium text-gray-600 block mb-1.5">Condition</label>
                      <select
                        value={String(selectedStep.config.field ?? "reply")}
                        onChange={(e) => updateStepConfig(selectedStep.id, { ...selectedStep.config, field: e.target.value })}
                        className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 bg-white text-gray-700 focus:outline-none focus:border-indigo-400 focus:ring-1 focus:ring-indigo-400 cursor-pointer"
                      >
                        <option value="reply">If reply contains</option>
                        <option value="no_reply">If no reply</option>
                        <option value="keyword">If keyword match</option>
                      </select>
                    </div>
                    {selectedStep.config.field !== "no_reply" && (
                      <div>
                        <label className="text-xs font-medium text-gray-600 block mb-1.5">Quick Options</label>
                        <div className="flex flex-wrap gap-1.5">
                          {["1", "2", "3", "interested", "No reply"].map((opt) => (
                            <button
                              key={opt}
                              onClick={() => updateStepConfig(selectedStep.id, { ...selectedStep.config, value: opt })}
                              className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors whitespace-nowrap cursor-pointer ${
                                selectedStep.config.value === opt
                                  ? "bg-indigo-600 text-white border-indigo-600"
                                  : "bg-white text-gray-600 border-gray-200 hover:border-indigo-300"
                              }`}
                            >
                              {opt === "1" ? 'Reply is "1"' : opt === "2" ? 'Reply is "2"' : opt === "3" ? 'Reply is "3"' : opt}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Action: Assign to Agent */}
                {selectedStep.type === "action" && selectedStep.label.includes("Assign") && (
                  <div className="space-y-4">
                    <div>
                      <label className="text-xs font-medium text-gray-600 block mb-1.5">Assignment Method</label>
                      <select className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 bg-white text-gray-700 focus:outline-none focus:border-indigo-400 focus:ring-1 focus:ring-indigo-400 cursor-pointer">
                        <option>Use assignment rules (recommended)</option>
                        <option>Rajesh Kumar</option>
                        <option>Priya Sharma</option>
                        <option>Amit Patel</option>
                      </select>
                    </div>
                    <div className="flex items-center justify-between">
                      <label className="text-xs font-medium text-gray-600">Notify agent</label>
                      <input type="checkbox" defaultChecked className="rounded accent-indigo-600 cursor-pointer" />
                    </div>
                  </div>
                )}

                {/* Action: Update Stage */}
                {selectedStep.type === "action" && selectedStep.label.includes("Stage") && (
                  <div className="space-y-4">
                    <div>
                      <label className="text-xs font-medium text-gray-600 block mb-1.5">Stage</label>
                      <select className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 bg-white text-gray-700 focus:outline-none focus:border-indigo-400 focus:ring-1 focus:ring-indigo-400 cursor-pointer">
                        <option>New Inquiry</option>
                        <option>Site Visit Scheduled</option>
                        <option>Site Visit Done</option>
                        <option>Proposal Sent</option>
                        <option>Negotiation</option>
                        <option>Booking Done</option>
                        <option>Lost</option>
                      </select>
                    </div>
                  </div>
                )}

                {/* Trigger config */}
                {selectedStep.type === "trigger" && (
                  <div className="space-y-4">
                    <div>
                      <label className="text-xs font-medium text-gray-600 block mb-1.5">Trigger Event</label>
                      <p className="text-sm text-gray-900 font-medium">{selectedStep.label}</p>
                    </div>
                    {selectedStep.label.includes("Lead Created") && (
                      <div>
                        <label className="text-xs font-medium text-gray-600 block mb-1.5">Enabled for sources</label>
                        <div className="space-y-1.5">
                          {["IndiaMart", "JustDial", "Website Forms"].map((src) => (
                            <label key={src} className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                              <input type="checkbox" defaultChecked className="rounded accent-indigo-600" />
                              {src}
                            </label>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Generic action config fallback */}
                {selectedStep.type === "action" &&
                  !selectedStep.label.includes("WhatsApp") &&
                  !selectedStep.label.includes("Assign") &&
                  !selectedStep.label.includes("Stage") && (
                  <div className="text-xs text-gray-400 italic">
                    Configure action parameters here
                  </div>
                )}

                {/* Delete button */}
                <button
                  onClick={() => deleteStep(selectedStep.id)}
                  className="mt-6 w-full flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg border border-red-200 text-red-600 text-sm font-medium hover:bg-red-50 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-delete-bin-line" />
                  Remove Step
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Toast */}
      {toast && (
        <div className="fixed bottom-5 right-5 z-[100] px-4 py-3 rounded-lg shadow-sm text-sm font-medium animate-slide-in bg-green-500 text-white">
          <div className="flex items-center gap-2.5">
            <i className="ri-check-line text-base" />
            {toast.message}
          </div>
        </div>
      )}
    </CrmLayout>
  );
}